import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, HelpCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ParallaxHero,
  AnimatedBadge,
  RippleButton,
  staggerContainer,
} from '@/components/animations';
import { trackEvent, trackHeroCTA } from '@/lib/analytics';

interface HeroSectionProps {
  onOpenApplicabilityModal: () => void;
}

export function HeroSection({ onOpenApplicabilityModal }: HeroSectionProps) {
  const navigate = useNavigate();

  return (
    <ParallaxHero className="py-24 md:py-36">
      <div className="container">
        <motion.div
          className="max-w-4xl mx-auto text-center space-y-8"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <AnimatedBadge icon={<Shield className="w-4 h-4" />}>
            Consultoria Técnica Independente
          </AnimatedBadge>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-foreground">
            Recebeu uma recomendação técnica para o seu elevador e não sabe se é mesmo necessária?
          </h1>

          <motion.p
            className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Apoio técnico independente, baseado na{' '}
            <span className="text-foreground font-medium">EN 81</span>, para
            decidir com rigor antes de assumir custos, riscos ou
            responsabilidades legais.
          </motion.p>

          <motion.div
            className="pt-4 space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4 sm:px-0">
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  trackEvent('applicability_modal_open', {
                    location: 'hero',
                    page_path: window.location.pathname,
                  });
                  trackHeroCTA('primary', 'Ver se este serviço se aplica ao meu caso');
                  onOpenApplicabilityModal();
                }}
                className="gap-2 text-base px-6 sm:px-8 py-5 sm:py-6 min-h-[56px] w-full sm:w-auto whitespace-normal text-center leading-tight touch-manipulation border-primary text-primary hover:bg-primary/5"
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                Ver se este serviço se aplica ao meu caso
              </Button>

              <RippleButton
                size="lg"
                onClick={() => navigate('/members/auth')}
                className="gap-2 text-base px-6 sm:px-8 py-5 sm:py-6 min-h-[56px] w-full sm:w-auto whitespace-normal text-center leading-tight touch-manipulation"
              >
                <span className="flex items-center justify-center gap-2 flex-wrap">
                  Criar conta gratuita
                  <ArrowRight className="w-5 h-5 flex-shrink-0" />
                </span>
              </RippleButton>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </ParallaxHero>
  );
}
